import * as React from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HistoryIcon from '@mui/icons-material/History';
import { ScheduleEntry } from '../../../domain/ScheduleEntry';
import { useTranslation } from 'react-i18next';

interface Props {
    isEditable: boolean;
    entry: ScheduleEntry;
    onEdit?: () => void;
    onViewPublisherHistory?: () => void;
    onViewAssignmentHistory?: () => void;
    onSendNotification?: () => void;
    onDownload?: () => void;
    onAutoArrange?: () => void;
}

const ScheduleTableRowActionsMenu = (props: Props) => {
    const { t } = useTranslation();
    const { isEditable, entry, onEdit, onViewPublisherHistory, onViewAssignmentHistory, onSendNotification, onDownload, onAutoArrange } = props;
    const canSendNotification = Boolean(onSendNotification && entry.id && entry.speakerId);
    const canViewPublisherHistory = Boolean(onViewPublisherHistory && entry.speakerId);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isOpen = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const menuItems: Array<{
        key: string;
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
        disabled?: boolean;
    }> = [];

    if (isEditable) {
        if (onDownload && entry.id) {
            menuItems.push({
                key: 'download',
                label: t('download_s89'),
                icon: <PictureAsPdfIcon color="primary" fontSize="small" />,
                onClick: () => {
                    onDownload();
                    handleMenuClose();
                },
            });
        }

        menuItems.push({
            key: 'edit',
            label: t('edit_entry'),
            icon: <EditIcon color="primary" fontSize="small" />,
            onClick: () => {
                onEdit && onEdit();
                handleMenuClose();
            },
        });

        if (onAutoArrange) {
            menuItems.push({
                key: 'auto-arrange',
                label: t('auto_arrange_entry'),
                icon: <AutoFixHighIcon color="primary" fontSize="small" />,
                onClick: () => {
                    onAutoArrange();
                    handleMenuClose();
                },
            });
        }

        menuItems.push({
            key: 'send-notification',
            label: t('send_notification'),
            icon: <SendIcon color="primary" fontSize="small" />,
            onClick: () => {
                onSendNotification && onSendNotification();
                handleMenuClose();
            },
            disabled: !canSendNotification,
        });

        if (onViewAssignmentHistory) {
            menuItems.push({
                key: 'assignment-history',
                label: t('assignment_history'),
                icon: <HistoryIcon color="primary" fontSize="small" />,
                onClick: () => {
                    onViewAssignmentHistory();
                    handleMenuClose();
                },
            });
        }

        menuItems.push({
            key: 'history',
            label: t('publisher_history'),
            icon: <CalendarMonthIcon fontSize="small" />,
            onClick: () => {
                onViewPublisherHistory && onViewPublisherHistory();
                handleMenuClose();
            },
            disabled: !canViewPublisherHistory,
        });
    }

    if (menuItems.length === 0) {
        return null;
    }

    return (
        <>
            <IconButton size='small' onClick={handleMenuOpen}>
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {menuItems.map((item) => (
                    <MenuItem
                        key={item.key}
                        onClick={item.onClick}
                        disabled={item.disabled}
                    >
                        <ListItemIcon>
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ScheduleTableRowActionsMenu;